import { useCallback, useEffect, useRef, useState } from 'react';
import { API_V2 } from '../lib/axios/instances/API_V2';

const DISMISS_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const HEADER_NAME = 'x-ui-v';
const BROADCAST_CHANNEL_NAME = 'clickster-version';

export const useVersionCheck = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [serverVersion, setServerVersion] = useState<string>('');
  const dismissedAtRef = useRef<number>(0);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Read version dynamically to allow for test mocking
  const currentVersion = process.env.NEXT_PUBLIC_APP_VERSION || '';

  // Initialize BroadcastChannel for cross-tab communication
  useEffect(() => {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        channel.onmessage = (event: MessageEvent) => {
          if (event.data?.type === 'VERSION_UPDATE') {
            window.location.reload();
          } else if (event.data?.type === 'VERSION_DISMISS') {
            dismissedAtRef.current = Date.now();
            setShowDialog(false);
          }
        };
        broadcastChannelRef.current = channel;
      }
    } catch {
      // BroadcastChannel not supported
    }

    return () => {
      broadcastChannelRef.current?.close();
      broadcastChannelRef.current = null;
    };
  }, []);

  useEffect(() => {
    const interceptorId = API_V2.interceptors.response.use((response) => {
      const apiVersion = response.headers?.[HEADER_NAME];
      const uiVersion = process.env.NEXT_PUBLIC_APP_VERSION || '';

      // Skip if no version header or no current version to compare
      if (!apiVersion || !uiVersion) return response;

      // Skip if within dismiss cooldown
      if (Date.now() - dismissedAtRef.current < DISMISS_COOLDOWN_MS) return response;

      // Compare versions
      if (apiVersion !== uiVersion) {
        setServerVersion(apiVersion);
        setShowDialog(true);
      }

      return response;
    });

    return () => {
      API_V2.interceptors.response.eject(interceptorId);
    };
  }, []);

  const dismiss = useCallback(() => {
    dismissedAtRef.current = Date.now();
    setShowDialog(false);
    try {
      broadcastChannelRef.current?.postMessage({ type: 'VERSION_DISMISS' });
    } catch {
      // Ignore broadcast errors
    }
  }, []);

  const update = useCallback(() => {
    // Broadcast to other tabs to reload before reloading this tab
    try {
      broadcastChannelRef.current?.postMessage({ type: 'VERSION_UPDATE' });
    } catch {
      // Ignore broadcast errors
    }
    window.location.reload();
  }, []);

  return { showDialog, serverVersion, currentVersion, dismiss, update };
};
